import { CreateDocumentRevisionForm, DocumentRevision, Party, RevisionWorkflow, WorkflowStep } from '../../types';
import projectsService, { AuxFileUploadEntry } from '../../services/projectsService';
import {
  RevisionFormValues,
  RevisionWorkflowFormValues,
  buildDefaultRevisionFormValues,
  buildDefaultRevisionWorkflowFormValues,
  hasWorkflowParties,
} from './formTypes';
import { AuxFileDraft } from './AuxiliaryFilesWidget';

export const auxDraftsToUploadEntries = (drafts: AuxFileDraft[]): AuxFileUploadEntry[] =>
  drafts
    .filter((draft) => !draft.sizeError && draft.file.size <= 50 * 1024 * 1024)
    .map((draft) => ({
      file: draft.file,
      category: draft.category,
      description: draft.description,
    }));

export const uploadAuxDraftsForRevision = async (
  projectId: number,
  documentId: number,
  revisionId: number,
  drafts: AuxFileDraft[]
): Promise<void> => {
  const entries = auxDraftsToUploadEntries(drafts);
  if (entries.length === 0) {
    return;
  }
  await projectsService.uploadRevisionAuxFiles(projectId, documentId, revisionId, entries);
};

export const workflowFormToSteps = (
  workflow: RevisionWorkflowFormValues,
  parties: Party[]
): WorkflowStep[] => {
  const steps: WorkflowStep[] = [];

  if (workflow.author_party_id) {
    const authorParty = parties.find((p) => p.id === workflow.author_party_id);
    steps.push({
      party_name: authorParty?.name || '',
      party_trigram: authorParty?.trigram || '',
      backup_party_name: null,
      backup_party_trigram: null,
      role: 'author',
      party_id: workflow.author_party_id,
    });
  }

  workflow.checkers.forEach((checker) => {
    const party = parties.find((p) => p.id === checker.party_id);
    steps.push({
      party_name: party?.name || '',
      party_trigram: party?.trigram || '',
      backup_party_name: null,
      backup_party_trigram: null,
      role: 'checker',
      party_id: checker.party_id,
      step_order: checker.step_order,
    });
  });

  workflow.approvers.forEach((approver) => {
    const party = parties.find((p) => p.id === approver.party_id);
    steps.push({
      party_name: party?.name || '',
      party_trigram: party?.trigram || '',
      backup_party_name: null,
      backup_party_trigram: null,
      role: 'approver',
      party_id: approver.party_id,
      step_order: approver.step_order,
    });
  });

  return steps;
};

export const workflowStepsToForm = (workflow: WorkflowStep[]): RevisionWorkflowFormValues => {
  const author = workflow.find((s) => s.role === 'author');
  const checkers = workflow
    .filter((s) => s.role === 'checker' && s.party_id)
    .map((s) => ({
      party_id: s.party_id!,
      step_order: s.step_order ?? 1,
    }));
  const approvers = workflow
    .filter((s) => s.role === 'approver' && s.party_id)
    .map((s) => ({
      party_id: s.party_id!,
      step_order: s.step_order ?? 1,
    }));

  return {
    author_party_id: author?.party_id,
    checkers,
    approvers,
  };
};

export const buildCreateRevisionPayload = (
  documentId: number,
  revision: RevisionFormValues,
  workflow: RevisionWorkflowFormValues
): CreateDocumentRevisionForm => ({
  document_id: documentId,
  revision_name_id: revision.revision_name_id,
  step: revision.step,
  start_date: revision.start_date || undefined,
  expected_issue_date: revision.expected_issue_date || undefined,
  status: 'pending',
  author_party_id: workflow.author_party_id,
  checkers: workflow.checkers,
  approvers: workflow.approvers,
  backup_parties: [],
});

export const buildDefaultRevisionState = () => ({
  revision: buildDefaultRevisionFormValues(),
  workflow: buildDefaultRevisionWorkflowFormValues(),
});

export interface LastDocumentWorkflowTemplate {
  workflow: RevisionWorkflowFormValues;
  sourceLabel: string;
  sourceRevisionId: number;
}

export function workflowPartiesToFormValues(
  parties: Array<{ role: string; party_id: number; step_order: number }>
): RevisionWorkflowFormValues {
  const author = parties.find((party) => party.role === 'author');
  return {
    author_party_id: author?.party_id,
    checkers: parties
      .filter((party) => party.role === 'checker')
      .map((party) => ({ party_id: party.party_id, step_order: party.step_order })),
    approvers: parties
      .filter((party) => party.role === 'approver')
      .map((party) => ({ party_id: party.party_id, step_order: party.step_order })),
  };
}

export function cloneRevisionWorkflowForm(
  workflow: RevisionWorkflowFormValues
): RevisionWorkflowFormValues {
  return {
    author_party_id: workflow.author_party_id,
    checkers: workflow.checkers.map((checker) => ({ ...checker })),
    approvers: workflow.approvers.map((approver) => ({ ...approver })),
  };
}

export function findLastDocumentWorkflow(
  revisions: DocumentRevision[],
  workflowsByRevisionId: Record<number, RevisionWorkflow | undefined>,
  excludeRevisionId?: number
): LastDocumentWorkflowTemplate | null {
  const sorted = [...revisions].sort(
    (a, b) =>
      (b.revision_counter ?? b.id) - (a.revision_counter ?? a.id) ||
      b.id - a.id
  );

  for (const revision of sorted) {
    if (excludeRevisionId !== undefined && revision.id === excludeRevisionId) {
      continue;
    }

    const workflow = workflowsByRevisionId[revision.id];
    if (!workflow?.parties?.length) {
      continue;
    }

    const formValues = workflowPartiesToFormValues(workflow.parties);
    if (!hasWorkflowParties(formValues)) {
      continue;
    }

    const name = revision.revision_name || 'Rev';
    const step = revision.step ?? '';
    return {
      workflow: formValues,
      sourceLabel: `${name}${step}`,
      sourceRevisionId: revision.id,
    };
  }

  return null;
}
